import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTasksStore } from '../stores/tasksStore';
import { useRelanceDeactivationStore } from '../stores/relanceDeactivationStore';
import { useLanguageStore } from '../stores/languageStore';
import { mapDailyTasksToGroups, mapExpectedRelanceToTaskItem, mapRelanceItemToTaskItem } from '../utils/mappers/taskMapper';
import { getCategoryRoutePrefix } from '../utils/relance/taskCategoryUtils';
import { formatShortDate, formatWeekdayDate } from '../utils/formatters';
import { TaskCategory } from '../types/enums';
import type { ExpectedRelanceDto, RelanceListItemDto } from '../types/customerFollowUp/relance';
import type { RelanceAgendaEntries, RelanceAgendaEntry } from '../types/customerFollowUp/relanceAgenda';

/** Today's relances with the ones sent today, the snoozed, ignored and expected ones. */
export function useRelanceAgendaEntries(): RelanceAgendaEntries {
    const { t } = useTranslation();
    const lang = useLanguageStore(s => s.lang);
    const dailyTasks = useTasksStore(s => s.dailyTasks);
    const completedTaskIds = useTasksStore(s => s.completedTaskIds);
    const deactivatedItems = useRelanceDeactivationStore(s => s.deactivatedItems);
    const expectedItems = useRelanceDeactivationStore(s => s.expectedItems);

    return useMemo(() => {
        const now = new Date().toISOString();
        const today = formatShortDate(now, lang);
        const ranked: RelanceAgendaEntry[] = dailyTasks
            ? mapDailyTasksToGroups(dailyTasks, completedTaskIds, t, lang).flatMap(g => g.items.map(item => ({
                item,
                routePrefix: g.routePrefix,
                status: item.isCompleted ? 'sent' as const : 'due' as const,
                dueAt: now,
                dateLabel: today,
                note: null,
            })))
            : [];
        const routePrefix = getCategoryRoutePrefix(TaskCategory.CUSTOMER_RELANCE);
        const toEntry = (dto: RelanceListItemDto, status: 'snoozed' | 'ignored', dueAt: string): RelanceAgendaEntry => ({
            item: mapRelanceItemToTaskItem(dto, lang),
            routePrefix,
            status,
            dueAt,
            dateLabel: formatWeekdayDate(dueAt, lang),
            note: dto.deactivationNote,
        });

        // A send the list already shows as sent, still active on the server until it is recorded, is not repeated.
        const shownIds = new Set(ranked.map(e => e.item.id));
        const sentFromServer = (dailyTasks?.sentRelances ?? [])
            .filter(dto => !shownIds.has(dto.id))
            .map((dto): RelanceAgendaEntry => {
                const sentAt = dto.sentAt ?? now;
                return {
                    item: mapRelanceItemToTaskItem(dto, lang, true),
                    routePrefix,
                    status: 'sent',
                    dueAt: sentAt,
                    dateLabel: formatShortDate(sentAt, lang),
                    note: null,
                };
            });
        const daily = [
            ...ranked.filter(e => e.status !== 'sent'),
            ...ranked.filter(e => e.status === 'sent'),
            ...sentFromServer,
        ];

        // These dates are the start of a UTC day, so they are formatted in UTC to keep their calendar day.
        const snoozed = deactivatedItems
            .filter((i): i is RelanceListItemDto & { snoozeUntil: string } => i.snoozeUntil != null)
            .sort((a, b) => a.snoozeUntil.localeCompare(b.snoozeUntil))
            .map(dto => toEntry(dto, 'snoozed', dto.snoozeUntil));

        const ignored = deactivatedItems
            .filter(i => i.snoozeUntil == null)
            .sort((a, b) => (b.deactivatedAt ?? '').localeCompare(a.deactivatedAt ?? ''))
            .map(dto => toEntry(dto, 'ignored', dto.deactivatedAt ?? now));

        const toExpectedEntry = (dto: ExpectedRelanceDto): RelanceAgendaEntry => {
            const dueAt = `${dto.expectedOn}T00:00:00Z`;
            return {
                item: mapExpectedRelanceToTaskItem(dto, lang),
                routePrefix,
                status: 'expected',
                dueAt,
                dateLabel: formatWeekdayDate(dueAt, lang),
                note: null,
                relanceId: dto.relanceId ?? null,
            };
        };
        const expected = expectedItems.map(toExpectedEntry);
        // A stable sort, so a snoozed relance stays ahead of an expected one on the same day.
        const upcoming = [...snoozed, ...expected].sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt));

        return { today: daily, snoozed, ignored, expected, upcoming };
    }, [dailyTasks, completedTaskIds, deactivatedItems, expectedItems, t, lang]);
}
